import cn from 'classnames';
import { STREAM_ADMIN_ROLES, StreamChatMessageAction } from '@features/components/Chat/constants';
import { StreamUserRole } from '@features/components/Chat/types';
import { isClinicalDataSharedMessage } from '@features/components/Chat/utils';
import {
  MessageOptions,
  MessageOptionsProps,
  MessageProvider,
  useChatContext,
  useMessageContext,
} from 'stream-chat-react';
import type { KeyboardEvent, MouseEvent } from 'react';

type CustomMessageOptionsProps = MessageOptionsProps & {
  isReadOnly?: boolean;
  userOptions: StreamChatMessageAction[];
};

export const CustomMessageOptions = (props: CustomMessageOptionsProps) => {
  const { isReadOnly, userOptions, ...messageOptionsProps } = props;
  const { client } = useChatContext();
  const userID = client?.user?.id;

  const context = useMessageContext();
  const { message } = context;

  const currentUserRole = message.user?.role;
  const isOwnMessage = message.user?.id === userID;
  const isModerator = STREAM_ADMIN_ROLES.includes(currentUserRole as StreamUserRole);

  const isClinicalDataShared = isClinicalDataSharedMessage(message);

  const getMessageActions = () => {
    if (isReadOnly) {
      return [];
    }

    const canEditMessage = isOwnMessage && !isClinicalDataShared;
    const canDeleteMessage = canEditMessage && isModerator;

    const permissionActions = [StreamChatMessageAction.Edit, StreamChatMessageAction.Delete];

    return userOptions.filter((action) => {
      if (action === StreamChatMessageAction.Edit) {
        return canEditMessage;
      }
      if (action === StreamChatMessageAction.Delete) {
        return canDeleteMessage;
      }
      return !permissionActions.includes(action);
    });
  };

  const stopClickPropagation = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const stopKeyPropagation = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
    }
  };

  return (
    <div
      className={cn('str-chat__message-options-wrapper', { 'read-only': !getMessageActions().length })}
      role="button"
      tabIndex={0}
      onClick={stopClickPropagation}
      onKeyDown={stopKeyPropagation}
    >
      <MessageProvider value={{ ...context, getMessageActions }}>
        <MessageOptions {...messageOptionsProps} />
      </MessageProvider>
    </div>
  );
};
